import { useId, useState } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useI18n } from '../i18n/useI18n';

/**
 * Every control has a real `<label for>`, hints and errors are wired through
 * `aria-describedby`, and an invalid field says so in text as well as colour.
 */

interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

function FieldShell({ label, hint, error, children }: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const described = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((v): v is string => v !== null)
    .join(' ');

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children({
        id,
        describedBy: described === '' ? undefined : described,
        invalid: error !== undefined,
      })}
      {hint !== undefined && (
        <p className="hint" id={hintId}>
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p className="err" id={errorId}>
          <span aria-hidden="true">✕</span>
          {error}
        </p>
      )}
    </div>
  );
}

type TextFieldProps = {
  label: string;
  hint?: string;
  error?: string;
  /** Machine text — identifiers, targets, fingerprints. */
  mono?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'>;

export function TextField({ label, hint, error, mono = false, ...input }: TextFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      {({ id, describedBy, invalid }) => (
        <input
          {...input}
          id={id}
          className={mono ? 'input mono' : 'input'}
          {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
          {...(invalid ? { 'aria-invalid': true } : {})}
        />
      )}
    </FieldShell>
  );
}

/** One option a picker offers: what is stored, and what a person reads. */
export interface PickerOption {
  value: string;
  label: string;
  /** Beside the label, dimmer: an id, an age, whatever tells two apart. */
  detail?: string;
}

type TagPickerProps = {
  label: string;
  hint?: string;
  error?: string;
  /** What may be chosen. */
  options: readonly PickerOption[];
  /** What is chosen, in the order it was chosen. */
  chosen: readonly string[];
  onChange: (chosen: string[]) => void;
  placeholder?: string;
  /** Read out to somebody removing a chip. */
  removeLabel: string;
};

/**
 * Several values from a list, kept as chips inside the box that filters it.
 *
 * A picker rather than a multiple `<select>`: a list that admits three kinds
 * out of nine is read as three words, and a multi-select shows nine rows with
 * three of them shaded. Typing narrows, Enter takes the first match, Backspace
 * on an empty box takes back the last chip — and every chip has a button, so a
 * keyboard and a mouse remove one the same way.
 */
export function TagPicker({
  label,
  hint,
  error,
  options,
  chosen,
  onChange,
  placeholder,
  removeLabel,
}: TagPickerProps) {
  const [query, setQuery] = useState('');
  const remaining = options.filter((option) => !chosen.includes(option.value));
  const needle = query.trim().toLowerCase();
  const matching =
    needle === ''
      ? remaining
      : remaining.filter(
          (option) =>
            option.label.toLowerCase().includes(needle) ||
            option.value.toLowerCase().includes(needle),
        );

  const take = (value: string) => {
    onChange([...chosen, value]);
    setQuery('');
  };

  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      {({ id, describedBy, invalid }) => (
        <div className={invalid ? 'tagbox invalid' : 'tagbox'}>
          <div className="tagbox-chips">
            {chosen.map((value) => {
              const option = options.find((candidate) => candidate.value === value);
              return (
                <span key={value} className="chip">
                  {option?.label ?? value}
                  <button
                    type="button"
                    className="chip-x"
                    aria-label={`${removeLabel}: ${option?.label ?? value}`}
                    onClick={() => {
                      onChange(chosen.filter((kept) => kept !== value));
                    }}
                  >
                    ✕
                  </button>
                </span>
              );
            })}
            <input
              id={id}
              className="tagbox-input"
              value={query}
              placeholder={chosen.length === 0 ? placeholder : ''}
              autoComplete="off"
              {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
              {...(invalid ? { 'aria-invalid': true } : {})}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && matching.length > 0) {
                  event.preventDefault();
                  take(matching[0].value);
                  return;
                }
                if (event.key === 'Backspace' && query === '' && chosen.length > 0) {
                  onChange(chosen.slice(0, -1));
                }
              }}
            />
          </div>
          {matching.length > 0 && (
            <ul className="tagbox-options">
              {matching.slice(0, 8).map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    onClick={() => {
                      take(option.value);
                    }}
                  >
                    <span>{option.label}</span>
                    {option.detail !== undefined && (
                      <span className="t-sub">{option.detail}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </FieldShell>
  );
}

type TextAreaFieldProps = {
  label: string;
  hint?: string;
  error?: string;
  mono?: boolean;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'className'>;

export function TextAreaField({ label, hint, error, mono = false, ...area }: TextAreaFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      {({ id, describedBy, invalid }) => (
        <textarea
          {...area}
          id={id}
          className={mono ? 'input mono' : 'input'}
          {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
          {...(invalid ? { 'aria-invalid': true } : {})}
        />
      )}
    </FieldShell>
  );
}

type SelectFieldProps = {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className' | 'children'>;

export function SelectField({ label, hint, error, children, ...select }: SelectFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      {({ id, describedBy, invalid }) => (
        <select
          {...select}
          id={id}
          className="input"
          {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
          {...(invalid ? { 'aria-invalid': true } : {})}
        >
          {children}
        </select>
      )}
    </FieldShell>
  );
}

/**
 * One choice of a filter.
 *
 * `count` is how many rows the choice leads to, printed in the button. It is
 * what saves a table of two rows standing beside the list it counts, and it is
 * only ever a number somebody else worked out — nothing here counts anything.
 * `marker` is a dot for the one choice that is happening now.
 */
export interface FilterOption<T extends string> {
  value: T;
  label: string;
  count?: number;
  marker?: boolean;
}

/** Segmented filter. Buttons, not tabs — they filter a list, they do not switch panels. */
export function SegmentedFilter<T extends string>({
  legend,
  value,
  options,
  onChange,
}: {
  legend: string;
  value: T;
  options: FilterOption<T>[];
  onChange: (value: T) => void;
}) {
  const { formatNumber } = useI18n();

  return (
    <div className="seg" role="group" aria-label={legend}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
          {option.count !== undefined && <span className="count">{formatNumber(option.count)}</span>}
          {option.marker === true && <span className="live-dot" aria-hidden="true" />}
        </button>
      ))}
    </div>
  );
}

/**
 * The one row above a table: what narrows it, and the box that searches it.
 *
 * It exists so that the counts live in the buttons. A filter whose buttons
 * carry their own numbers is a filter and a summary at once, which is what
 * keeps a two-row table of totals off the screen.
 */
export function FilterBar({ filters, search }: { filters: ReactNode; search?: ReactNode }) {
  return (
    <div className="toolbar">
      {filters}
      {search !== undefined && <div className="toolbar-search">{search}</div>}
    </div>
  );
}

export function SearchInput({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className="search-inline">
      <label className="visually-hidden" htmlFor={id}>
        {label}
      </label>
      <span aria-hidden="true" className="faint">
        ⌕
      </span>
      <input
        id={id}
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}
